/**
 * DataTable - Generisk tabellkomponent
 *
 * Kolumndefinitioner (key, label, align, sortable, render)
 * Stöd för sortering, sidindelning, CSV-export, sök, resizable columns, radval, sticky header
 * Konsekvent styling (Tailwind: divide-y, border-border)
 * CSV-export använder semicolon (;) som delimiter för svensk/europeisk Excel-kompatibilitet.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { exportTableToCsv } from '@/utils/tableExport';

export type Align = 'left' | 'right' | 'center';

export interface DataTableColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  align?: Align;
  sortable?: boolean;
  /** Nyckel för sortering (default: key). Använd för beräknade kolumner. Stödjer dot-notation (t.ex. ecoMetrics.estimatedFuelWaste). */
  sortKey?: string;
  /** Custom sorteringsvärde (överordnar sortKey). */
  sortValue?: (row: T) => number | string;
  /** Kolumnbredd i px (resizable eller fast bredd vid horizontalScroll). */
  width?: number;
  render?: (value: unknown, row: T) => React.ReactNode;
  /** Custom CSV-värde (annars används String(value)) */
  csvValue?: (value: unknown, row: T) => string;
  /** Kolumnen får en filter-textbox när columnFilters-feature är på. */
  filterable?: boolean;
  /** Nyckel som rapporteras till server-side filter (default: sortKey ?? key). */
  filterKey?: string;
}

export interface DataTableFeatures {
  /** CSV-exportknapp */
  csvExport?: boolean;
  /** Sökfält för att filtrera rader */
  search?: boolean;
  /** Dra för att ändra kolumnbredd */
  resizableColumns?: boolean;
  /** Checkboxar för radval */
  rowSelection?: boolean;
  /** Fast rubrik vid scroll */
  stickyHeader?: boolean;
  /** Horisontell scroll i tabellens nederkant när innehållet är bredare än vyn */
  horizontalScroll?: boolean;
  /** Visa en filterrad under rubrikerna med en textbox per filterbar kolumn */
  columnFilters?: boolean;
}

export interface DataTableProps<T = Record<string, unknown>> {
  /** Tabellrubrik */
  title?: string;
  /** Kolumndefinitioner */
  columns: DataTableColumn<T>[];
  /** Data att visa */
  data: T[];
  /** Nyckel för rad-identitet (för key prop) */
  rowKey?: string | ((row: T) => string | number);
  /** Antal rader per sida (0 = ingen sidindelning) */
  pageSize?: number;
  /** Tomt meddelande */
  emptyMessage?: string;
  /** Funktioner som kan aktiveras per användning */
  features?: DataTableFeatures;
  /** Callback vid radval (kräver rowSelection) */
  onSelectionChange?: (selected: T[]) => void;
  /** Callback vid radklick */
  onRowClick?: (row: T) => void;
  /**
   * Kontrollerad (server-side) sortering. När satt sorterar tabellen INTE internt –
   * den visar `data` som den är och rapporterar sorteringsändringar via `onSortChange`.
   * Används för server-paginerade listor där hela registret ska sorteras.
   */
  manualSort?: {
    sortBy: string | null;
    sortDir: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortDir: 'asc' | 'desc') => void;
  };
  /**
   * Kontrollerad (server-side) per-kolumn filtrering. När satt filtrerar tabellen INTE
   * internt – den visar `data` som den är och rapporterar filterändringar via `onFilterChange`.
   * Nyckeln är kolumnens filterKey (default sortKey ?? key) som mappas mot server-whitelist.
   */
  manualColumnFilters?: {
    filters: Record<string, string>;
    onFilterChange: (key: string, value: string) => void;
  };
}

/**
 * Filter-textbox med lokal state och debounce, så att server-side filter inte hamras
 * vid varje tangenttryck. Synkar mot externt värde (t.ex. när filter nollställs).
 */
function DebouncedFilterInput({
  value,
  placeholder,
  onChange,
  align,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  align?: Align;
}) {
  const [local, setLocal] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    if (local === value) return;
    const handle = setTimeout(() => onChangeRef.current(local), 400);
    return () => clearTimeout(handle);
  }, [local, value]);

  const textAlign = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  return (
    <input
      type="search"
      value={local}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setLocal(e.target.value)}
      className={`w-full px-2 py-1 text-xs font-normal normal-case border border-border rounded bg-bgBase text-textDefault placeholder:text-textSecondary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary ${textAlign}`}
    />
  );
}

function rowMatchesSearch<T>(row: T, columns: DataTableColumn<T>[], searchText: string): boolean {
  if (!searchText.trim()) return true;
  const q = searchText.toLowerCase().trim();
  return columns.some(col => {
    const val = row[col.key as keyof T];
    const str = col.csvValue ? col.csvValue(val, row) : String(val ?? '');
    return str.toLowerCase().includes(q);
  });
}

export function DataTable<T extends object>({
  title,
  columns,
  data,
  rowKey = 'id',
  pageSize = 0,
  emptyMessage = 'Ingen data att visa',
  features = {},
  onSelectionChange,
  onRowClick,
  manualSort,
  manualColumnFilters,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const {
    csvExport = false,
    search = false,
    resizableColumns = false,
    rowSelection = false,
    stickyHeader = false,
    horizontalScroll = false,
    columnFilters = false,
  } = features;

  const cellWrapClass = horizontalScroll ? 'whitespace-nowrap' : '';

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [localColumnFilters, setLocalColumnFilters] = useState<Record<string, string>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const w: Record<string, number> = {};
    columns.forEach(c => { if (c.width != null) w[c.key] = c.width; });
    return w;
  });
  const resizeStartRef = useRef<{ colKey: string; startX: number; startW: number } | null>(null);

  const isManualSort = !!manualSort;
  const effectiveSortKey = isManualSort ? manualSort!.sortBy : sortKey;
  const effectiveSortDir = isManualSort ? manualSort!.sortDir : sortDir;

  /** Sorteringsidentitet för en kolumn: i kontrollerat läge används sortKey (matchar server-whitelist). */
  const sortIdentity = useCallback(
    (col: DataTableColumn<T>) => (isManualSort ? (col.sortKey ?? col.key) : col.key),
    [isManualSort]
  );

  const handleSort = useCallback((key: string) => {
    const col = columns.find(c => c.key === key);
    if (!col?.sortable) return;
    if (isManualSort) {
      const id = col.sortKey ?? col.key;
      const nextDir: 'asc' | 'desc' =
        manualSort!.sortBy === id && manualSort!.sortDir === 'asc' ? 'desc' : 'asc';
      manualSort!.onSortChange(id, nextDir);
      return;
    }
    const nextDir: 'asc' | 'desc' = sortKey === key && sortDir === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDir(nextDir);
  }, [columns, isManualSort, manualSort, sortKey, sortDir]);

  const isManualColumnFilters = !!manualColumnFilters;

  /** Filteridentitet för en kolumn: filterKey > sortKey > key (mappas mot server-whitelist). */
  const filterIdentity = useCallback(
    (col: DataTableColumn<T>) => col.filterKey ?? col.sortKey ?? col.key,
    []
  );

  const getColumnFilterValue = useCallback(
    (col: DataTableColumn<T>): string =>
      isManualColumnFilters
        ? manualColumnFilters!.filters[filterIdentity(col)] ?? ''
        : localColumnFilters[col.key] ?? '',
    [isManualColumnFilters, manualColumnFilters, filterIdentity, localColumnFilters]
  );

  const setColumnFilterValue = useCallback(
    (col: DataTableColumn<T>, value: string) => {
      if (isManualColumnFilters) {
        manualColumnFilters!.onFilterChange(filterIdentity(col), value);
      } else {
        setPage(0);
        setLocalColumnFilters(prev => ({ ...prev, [col.key]: value }));
      }
    },
    [isManualColumnFilters, manualColumnFilters, filterIdentity]
  );

  const filteredData = useMemo(() => {
    let rows = searchText.trim()
      ? data.filter(row => rowMatchesSearch(row, columns, searchText))
      : data;
    // Lokal per-kolumn filtrering (endast när server-side inte styr filtren).
    if (columnFilters && !isManualColumnFilters) {
      const active = Object.entries(localColumnFilters).filter(([, v]) => v.trim() !== '');
      if (active.length > 0) {
        rows = rows.filter(row =>
          active.every(([key, val]) => {
            const col = columns.find(c => c.key === key);
            if (!col) return true;
            const raw = row[col.key as keyof T];
            const str = col.csvValue ? col.csvValue(raw, row) : String(raw ?? '');
            return str.toLowerCase().includes(val.toLowerCase().trim());
          })
        );
      }
    }
    return rows;
  }, [data, columns, searchText, columnFilters, isManualColumnFilters, localColumnFilters]);

  const getSortValue = useCallback((row: T, key: string): unknown => {
    const obj = row as Record<string, unknown>;
    if (key.includes('.')) {
      return key.split('.').reduce((o: unknown, k) => (o != null && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined), obj);
    }
    return obj[key];
  }, []);

  const sortedData = useMemo(() => {
    // Kontrollerad sortering: data är redan sorterad server-side, sortera inte om lokalt.
    if (isManualSort) return filteredData;
    if (!sortKey) return filteredData;
    const col = columns.find(c => c.key === sortKey);
    return [...filteredData].sort((a, b) => {
      let aVal: unknown;
      let bVal: unknown;
      if (col?.sortValue) {
        aVal = col.sortValue(a);
        bVal = col.sortValue(b);
      } else {
        const keyForSort = col?.sortKey ?? sortKey;
        aVal = getSortValue(a, keyForSort);
        bVal = getSortValue(b, keyForSort);
      }
      const cmp = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal ?? '').localeCompare(String(bVal ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredData, sortKey, sortDir, columns, getSortValue, isManualSort]);

  const paginatedData = useMemo(() => {
    if (pageSize <= 0) return sortedData;
    const start = page * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, page, pageSize]);

  const totalPages = pageSize > 0 ? Math.ceil(sortedData.length / pageSize) : 1;
  const hasPages = totalPages > 1;

  const getRowKey = useCallback((row: T, idx: number): string | number => {
    if (typeof rowKey === 'function') return rowKey(row);
    const key = row[rowKey as keyof T];
    return key != null ? String(key) : idx;
  }, [rowKey]);

  const handleSelectRow = useCallback((row: T, idx: number) => {
    const key = getRowKey(row, idx);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      const newSelected = data.filter(r => next.has(getRowKey(r, -1)));
      onSelectionChange?.(newSelected);
      return next;
    });
  }, [getRowKey, data, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    const allKeys = new Set(paginatedData.map((r, i) => getRowKey(r, i)));
    const allSelected = allKeys.size > 0 && paginatedData.every((r, i) => selectedIds.has(getRowKey(r, i)));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        paginatedData.forEach((r, i) => next.delete(getRowKey(r, i)));
        const newSelected = data.filter(r => next.has(getRowKey(r, -1)));
        onSelectionChange?.(newSelected);
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        paginatedData.forEach((r, i) => next.add(getRowKey(r, i)));
        const newSelected = data.filter(r => next.has(getRowKey(r, -1)));
        onSelectionChange?.(newSelected);
        return next;
      });
    }
  }, [paginatedData, data, selectedIds, getRowKey, onSelectionChange]);

  const handleCsvExport = useCallback(() => {
    exportTableToCsv(columns, sortedData, `export-${new Date().toISOString().slice(0, 10)}.csv`);
  }, [columns, sortedData]);

  const handleResizeStart = useCallback((colKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    const w = columnWidths[colKey] ?? columns.find(c => c.key === colKey)?.width ?? 150;
    resizeStartRef.current = { colKey, startX: e.clientX, startW: w };
    const onMove = (e2: MouseEvent) => {
      if (!resizeStartRef.current) return;
      const { colKey: k, startX, startW } = resizeStartRef.current;
      const delta = e2.clientX - startX;
      const newW = Math.max(50, startW + delta);
      setColumnWidths(prev => ({ ...prev, [k]: newW }));
      resizeStartRef.current = { colKey: k, startX: e2.clientX, startW: newW };
    };
    const onUp = () => {
      resizeStartRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [columnWidths, columns]);

  const alignClass = (align?: Align) => {
    switch (align) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  };

  /** Rubrik använder flex (label + sorteringsikon); text-align på <th> påverkar inte flex-innehåll. */
  const headerInnerClass = (align?: Align) => {
    switch (align) {
      case 'center':
        return 'flex w-full items-center justify-center gap-1';
      case 'right':
        return 'flex w-full items-center justify-end gap-1';
      default:
        return 'inline-flex items-center gap-1';
    }
  };

  const getColWidth = (col: DataTableColumn<T>) => {
    if (resizableColumns) {
      const w = columnWidths[col.key] ?? col.width ?? 150;
      return { width: w, minWidth: 50 };
    }
    if (col.width != null) {
      return { width: col.width, minWidth: col.width };
    }
    return undefined;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        {title && (
          <h2 className="text-lg font-semibold text-textDefault">{title}</h2>
        )}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {search && (
            <input
              type="search"
              placeholder={t('dataTable.searchPlaceholder')}
              value={searchText}
              onChange={e => { setSearchText(e.target.value); setPage(0); }}
              className="px-3 py-1.5 text-sm border border-border rounded-md bg-bgBase text-textDefault placeholder:text-textSecondary focus:ring-2 focus:ring-primary focus:border-primary"
            />
          )}
          {csvExport && (
            <button
              type="button"
              onClick={handleCsvExport}
              disabled={sortedData.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-textDefault border border-border rounded-md hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('common.exportCsv')}
            >
              <ArrowDownTrayIcon className="w-4 h-4" aria-hidden />
              {t('common.exportCsv')}
            </button>
          )}
        </div>
      </div>
      <div
        className={`bg-bgElevated rounded-lg shadow-sm border border-border min-w-0 max-w-full ${
          horizontalScroll ? '' : 'overflow-hidden'
        }`}
      >
        <div
          className={[
            horizontalScroll ? 'overflow-x-auto max-w-full overscroll-x-contain' : 'overflow-x-auto',
            stickyHeader ? 'max-h-[60vh] overflow-y-auto' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <table
            className={`divide-y divide-border ${horizontalScroll ? 'w-max min-w-full' : 'min-w-full'}`}
            style={{ tableLayout: resizableColumns ? 'fixed' : undefined }}
          >
            <thead className={`bg-bgContainer ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
              <tr>
                {rowSelection && (
                  <th className="w-10 px-2 py-3">
                    <input
                      type="checkbox"
                      checked={paginatedData.length > 0 && paginatedData.every((r, i) => selectedIds.has(getRowKey(r, i)))}
                      ref={el => {
                        if (el) {
                          const some = paginatedData.some((r, i) => selectedIds.has(getRowKey(r, i)));
                          const all = paginatedData.length > 0 && paginatedData.every((r, i) => selectedIds.has(getRowKey(r, i)));
                          el.indeterminate = some && !all;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="rounded border-border"
                    />
                  </th>
                )}
                {columns.map(col => (
                  <th
                    key={col.key}
                    style={getColWidth(col)}
                    className={`px-4 py-3 text-xs font-medium text-textSecondary uppercase ${alignClass(col.align)} ${cellWrapClass} ${col.sortable ? 'cursor-pointer select-none hover:bg-black/5 dark:hover:bg-white/5' : ''} ${resizableColumns ? 'relative' : ''}`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <span className={headerInnerClass(col.align)}>
                      {col.label}
                      {col.sortable && (
                        effectiveSortKey === sortIdentity(col) ? (
                          effectiveSortDir === 'asc' ? (
                            <ChevronUpIcon className="w-4 h-4 text-primary shrink-0" aria-hidden />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4 text-primary shrink-0" aria-hidden />
                          )
                        ) : (
                          <ChevronUpDownIcon className="w-4 h-4 text-textSecondary shrink-0 opacity-60" aria-hidden />
                        )
                      )}
                    </span>
                    {resizableColumns && (
                      <span
                        role="separator"
                        className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30"
                        onMouseDown={e => handleResizeStart(col.key, e)}
                        aria-label={`Ändra bredd på ${col.label}`}
                      />
                    )}
                  </th>
                ))}
              </tr>
              {columnFilters && columns.some(c => c.filterable) && (
                <tr className="bg-bgContainer">
                  {rowSelection && <th className="w-10 px-2 py-2" />}
                  {columns.map(col => (
                    <th key={col.key} style={getColWidth(col)} className="px-2 py-2">
                      {col.filterable ? (
                        <DebouncedFilterInput
                          value={getColumnFilterValue(col)}
                          placeholder={t('dataTable.filterPlaceholder')}
                          align={col.align}
                          onChange={value => setColumnFilterValue(col, value)}
                        />
                      ) : null}
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (rowSelection ? 1 : 0)} className="px-4 py-12 text-center text-textSecondary">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr
                    key={getRowKey(row, idx)}
                    onClick={() => onRowClick?.(row)}
                    className={`hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${selectedIds.has(getRowKey(row, idx)) ? 'bg-primary/5' : ''}`}
                  >
                    {rowSelection && (
                      <td className="w-10 px-2 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(getRowKey(row, idx))}
                          onChange={() => handleSelectRow(row, idx)}
                          className="rounded border-border"
                        />
                      </td>
                    )}
                    {columns.map(col => {
                      const value = row[col.key as keyof T];
                      const content: React.ReactNode = col.render
                        ? col.render(value, row)
                        : (value != null ? String(value) : '');
                      return (
                        <td
                          key={col.key}
                          style={getColWidth(col)}
                          className={`px-4 py-3 text-sm text-textDefault ${alignClass(col.align)} ${cellWrapClass}`}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasPages && (
          <div className="px-4 py-2 border-t border-border flex items-center justify-between bg-bgContainer">
            <span className="text-sm text-textSecondary">
              Visar {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedData.length)} av {sortedData.length}
              {search && searchText && ` (${filteredData.length} träffar)`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 text-sm text-textDefault border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                Föregående
              </button>
              <span className="px-3 py-1 text-sm text-textSecondary">
                Sida {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1 text-sm text-textDefault border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                Nästa
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataTable;
